// Initializes Bootstrap Tooltips and Popovers wherever they appear in the DOM - including
// content rendered after the initial page load (Vue, Turbo, async fetches, etc.).
//
// Bootstrap only wires up an element when you explicitly call `new Tooltip(el)` /
// `new Popover(el)` on it; anything rendered later is inert until something re-runs that.
// Previously, individual Vue components handled this themselves by re-scanning the whole
// document in their own `updated()` hook. A single MutationObserver here replaces all of
// that, and is strictly cheaper and more correct:
//   - it only fires on actual DOM insertions/removals, not on every Vue reactive
//     re-render (which happens far more often, including renders that touch no
//     tooltip/popover element at all)
//   - each firing only scans the newly-added subtree, not the entire document
//   - the dataset.bsWidgetReady guard means an element is ever wired up exactly once,
//     instead of being reconstructed on every pass
//   - it's global, so it also catches content a component might forget to wire up itself
//     (this is how we found Inventory.vue's tooltip was silently never initializing)
// Call initBootstrapWidgets() once (already done in application_bs.js, which every layout
// loads) - the one constant cost is a single observer living for the page's lifetime,
// negligible next to what it replaced.
//
// Popovers with data-bs-trigger="manual" additionally get hover-open behavior that -
// unlike Bootstrap's own 'hover' trigger - stays open while the pointer is over the
// popover's own content. Bootstrap's hover trigger only tracks the trigger element, so it
// hides the popover the instant the pointer leaves it, even if it's now over the popup -
// making any interactive content inside (like a link) unreachable. Use this for popovers
// that need to hold a link or other interactive content.
import { Popover, Tooltip } from 'bootstrap';

const HOVER_POPOVER_CLOSE_DELAY_MS = 200;

function enableHoverPopover(el) {
  const popover = new Popover(el);
  let isOpen = false;

  let closeTimeout;
  const cancelClose = () => clearTimeout(closeTimeout);
  const scheduleClose = () => {
    cancelClose();
    closeTimeout = setTimeout(() => {
      isOpen = false;
      popover.hide();
    }, HOVER_POPOVER_CLOSE_DELAY_MS);
  };

  el.addEventListener('mouseenter', () => {
    cancelClose();
    // Chrome can fire a second, spurious mouseenter on the trigger right after the tip is
    // inserted/repositioned under the cursor. Calling show() again here would make Bootstrap
    // dispose and recreate the tip (and its listeners) for no reason, so only open once.
    if (isOpen) return;
    isOpen = true;
    popover.show();
    // The tip is inserted into the DOM synchronously by show(); don't wait for
    // 'shown.bs.popover', which only fires after the fade transition ends and can
    // arrive after the pointer has already reached the tip.
    popover.tip?.addEventListener('mouseenter', cancelClose);
    popover.tip?.addEventListener('mouseleave', scheduleClose);
  });
  el.addEventListener('mouseleave', (event) => {
    // If the pointer moved onto the tip itself, don't schedule a close.
    if (popover.tip?.contains(event.relatedTarget)) return;
    scheduleClose();
  });
}

const TOOLTIP_SELECTOR = '[data-bs-toggle="tooltip"]';
const HOVER_POPOVER_SELECTOR = '[data-bs-toggle="popover"][data-bs-trigger="manual"]';
const POPOVER_SELECTOR = '[data-bs-toggle="popover"]';

// Checked in order, so the more specific hover-popover selector is matched before the
// plain popover one.
const WIDGET_INITIALIZERS = [
  [TOOLTIP_SELECTOR, (el) => new Tooltip(el)],
  [HOVER_POPOVER_SELECTOR, enableHoverPopover],
  [POPOVER_SELECTOR, (el) => new Popover(el)],
];

const WIDGET_SELECTOR = `${TOOLTIP_SELECTOR}, ${POPOVER_SELECTOR}`;

function initWidget(el) {
  if (el.dataset.bsWidgetReady) return;
  el.dataset.bsWidgetReady = 'true';

  const match = WIDGET_INITIALIZERS.find(([selector]) => el.matches(selector));
  match?.[1](el);
}

function initWidgetsWithin(root) {
  if (root.matches?.(WIDGET_SELECTOR)) initWidget(root);
  root.querySelectorAll?.(WIDGET_SELECTOR).forEach(initWidget);
}

export function initBootstrapWidgets() {
  initWidgetsWithin(document.body);

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initWidgetsWithin(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
}
